// import libraries
import { useContext, useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router";
import { useTranslation } from "react-i18next";
import axios from "axios";
import moment from "moment";
// import files
import { env } from "../../env";
import { AuthContext } from "../../context";
// import components
import Layout from "../../layouts/Layout";
import ProfileLayout from "../../components/Profile/ProfileLayout";
import LoaderIcon from "../../components/Common/Icon/Loader";
import SchedulePopUp from "../../components/Profile/SchedulePopUp";
import WarningPopUp from "../../components/Profile/WarningPopUp";
import DeactivatePopUp from "../../components/Profile/DeactivatePopUp";
import ProfileComponent from "../../components/Profile/ProfileComponent";
import MapComponent from "../../components/Dashboard/MapComponent";
import TransactionComponent from "../../components/Profile/TransactionComponent";
import RecommendedProductsComponent from "../../components/Profile/RecommendedProductsComponent";
import AttachmentComponent from "../../components/Profile/AttachmentComponent";
import AgreementComponent from "../../components/Profile/AgreementComponent";
import AssetsComponent from "../../components/Profile/AssetsComponent";
import CouponComponent from "../../components/Profile/CouponComponent";
import TableLoaderIcon from "../../components/Common/Icon/TableLoader";
import DateFilterDropdown from "../../components/Profile/Transaction/DateFilterDropdown";

const ProfileView = () => {
    // variables
    const { t } = useTranslation();
    const { id } = useParams();
    const navigate = useNavigate();
    const authContext = useContext(AuthContext);
    const baseUrl = env.REACT_APP_API_URL;
    const token = localStorage.getItem('token');
    const activeTab = "border-b border-blue-normal text-blue-normal";
    const formatter = new Intl.NumberFormat('de-DE', {
        style: 'decimal',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    });
    // first
    const profileTab = localStorage.getItem('profile_tab');
    const [showTab, setShowTab] = useState(profileTab ? profileTab : 'profile');
    const storedVisitData = localStorage.getItem('visit');
    const initialVisitData = storedVisitData ? JSON.parse(storedVisitData) : {};
    const [visitTypeData, setVisitTypeData] = useState(initialVisitData);
    // boolean
    const [isLoader, setIsLoader] = useState(true);
    const [showSchedulePopUp, setShowSchedulePopUp] = useState(false);
    const [showWarningPopUp, setShowWarningPopUp] = useState(false);
    const [showDeactivePopUp, setShowDeactivePopUp] = useState(false);
    const [showMap, setShowMap] = useState(false);
    // list data
    const [visitTypeList, setVisitTypeList] = useState([]);
    const [deactivateReasonList, setDeactivateReasonList] = useState([]);
    // data
    const [customerData, setCustomerData] = useState({});
    const [ipData, setIpData] = useState({});
    const [markers, setmarkers] = useState([]);
    const [markerList, setMarkerList] = useState([]);
    const [userData, setUserData] = useState([]);
    const [recommendedProductList, setRecommendedProductList] = useState([]);
    const [attachmentList, setAttachmentList] = useState([]);
    const [agreementList, setAgreementList] = useState([]);
    const [allAssignedAssetList, setAllAssignedAssetList] = useState([]);
    const [couponList, setCouponList] = useState([]);
    const [transactionList, setTransactionList] = useState([]);
    const [defaultList, setDefaultList] = useState([]);
    const [oldTransactionData, setOldTransactionData] = useState([]);
    const [lastCalculatedBalance, setLastCalculatedBalance] = useState(0);
    const [rowCurrentPage, setCurrentPage] = useState(1);

    // single data
    const [totalCustomer, setTotalCustomer] = useState(0);
    const [defaultProductImage, setDefaultProductImage] = useState("");

    // loader for tab
    const [isRecommendedPrdouctLoader, setIsRecommendedPrdouctLoader] = useState(true);
    const [isMapLader, setIsMapLoader] = useState(true);
    const [isAttachmentLoader, setIsAttachmentLoader] = useState(true);
    const [isAgreementLoader, setIsAgreementLoader] = useState(true);
    const [isAssetsLoader, setIsAssetsLoader] = useState(true);
    const [isCouponLoader, setIsCouponLoader] = useState(true);

    const handlePageChange = (data) => {
        if((visitTypeData && visitTypeData.visitId)) {
            setShowWarningPopUp(!showWarningPopUp)
        } else {
            navigate(data);
        }  
    };

    useEffect(() => {
        const handlePopState = () => {
            handlePageChange(-1); // Pass -1 to navigate back
        };

        window.addEventListener("popstate", handlePopState);

        return () => {
            window.removeEventListener("popstate", handlePopState);
        };
    }, [visitTypeData]);

    useEffect(() => {
        const fetchData = async () => {
            await getTransactionListApi(baseUrl, token);
            await getDefaultProductImage(baseUrl, token);
            await getCustomerDataApi(baseUrl, token);
            // after loader
            getVisitTypeListApi(baseUrl, token);
            getDeactivateReasonListApi(baseUrl, token);
            getUserDataApi(baseUrl, token);
            getRecommendedProductListApi(baseUrl, token);
            getAttachmentListApi(baseUrl, token);
            getAgreementListApi(baseUrl, token);
            getAllAssignedAssetListApi(baseUrl, token);
            getCustomerCouponListApi(baseUrl, token);
        };
        fetchData();
    }, [baseUrl, token]);

    // function
    const handlePageLinkChange = (data) => {
        if((visitTypeData && visitTypeData.visitId)) {
            setShowWarningPopUp(!showWarningPopUp)
        } else {
            navigate(data);
        }  
    };
    const handleTabCheckDataFromChild = (data) => {
        if((visitTypeData && visitTypeData.visitId) && data.tab === 'profile') {
            setShowWarningPopUp(!showWarningPopUp)
        } else {
            if(data.link === '/logout') {
                authContext.logout();
            } else {
                navigate(data.link);
            }
            localStorage.removeItem("profile_tab");
        }
    };
    // api's
    const getDefaultProductImage = async (url, token) => {
        const headers = {
            "Accept" : "Application/json",
            "Content-Type" : "Application/json",
            "Authorization" : `Bearer ${token}`
        };

        await axios.get(url+"/api/User/GetDefaultImage",{ headers })
        .then((response) => {
            setDefaultProductImage(response.data.productDefaultImage);
        }).catch((error) => {
            console.log(error);
            if(error.response && (error.response.status === 401 || error.response.status === 404)) {
                localStorage.removeItem("token");
                localStorage.removeItem("userInfo");
                window.location.reload();
            }
        });
    };
    const getCustomerDataApi = async (url, token) => {
        await axios.post(url+"/api/Customer/GetCustomerDetail", {customerid: id},{ 
            headers: {
                "Content-Type" : "multipart/form-data",
                "Authorization" : `Bearer ${token}`
            }
        }).then((response) => {
            const customer = response.data[0];
            let truePartners = [];
            let falsePartners = [];
            
            if(customer.partner) {
                JSON.parse(customer.partner).map((partner, value) => {
                    if (partner.IsDefault) {
                        truePartners.push(partner);
                    } else {
                        falsePartners.push(partner);
                    }
                });
            }
            customer.mainPartner = truePartners;
            customer.partnerList = falsePartners;
            
            if(customer.addressLatitude != null && customer.addressLatitude !== '' && customer.addressLongitude != null && customer.addressLongitude !== '') {
                setmarkers([{ 
                    ...markers,
                    geocode: [customer.addressLatitude, customer.addressLongitude],
                    popUpData: {
                        "lastName" : customer.customerTitle ? customer.customerTitle : "",
                        "name" : customer.contactName ? customer.contactName : "",
                        "address" : customer.address,
                        "phone" : (customer.phone && customer.phone !== "NA") ? customer.phone : "",
                        "mobileNumber" : customer.mobileNumber ? customer.mobileNumber : "",
                        "date" : customer.lastVisitedDate ? customer.lastVisitedDate : ""
                    }
                }]);
            }
            setCustomerData(customer);
            setIsLoader(false);
            getIpJson();
        }).catch((error) => {
            console.log(error);
            if(error.response && (error.response.status === 401 || error.response.status === 404)) {
                localStorage.removeItem("token");
                localStorage.removeItem("userInfo");
                window.location.reload();
            }
        });
    };
    const getUserDataApi = async (url, token) => {
        setIsMapLoader(true);
        const headers = {
            "Accept" : "application/json",
            "Content-Type" : "application/json",
            "Authorization": `Bearer ${token}`
        };

        await axios.post(url+"/api/User/GetUserDetailsByUserID", {
            "UserName" : authContext.userInfo.userName
        },{ headers })
        .then((response) => {
            setUserData(response.data);
            getCustomerListRadiusWiseApi(url, token, response.data.radius ?? 0);
        }).catch((error) => {
            console.log(error);
            if(error.response && (error.response.status === 401 || error.response.status === 404)) {
                localStorage.removeItem("token");
                localStorage.removeItem("userInfo");
                window.location.reload();
            }
        });
    };
    const getCustomerListRadiusWiseApi = async(url, token, radius) => {
        const headers = {
            "Accept" : "Application/json",
            "Content-Type" : "Application/json",
            "Authorization" : `Bearer ${token}`
        };

        await axios.get(url+`/api/Customer/getcustomerlistradiuswise?customer_id=${id}&radius=${radius}`,{ headers })
        .then((response) => {
            if(response.data.length > 0) {
                setMarkerList([]);
                setTotalCustomer(response.data.length);
                response.data.forEach((value, index) => {
                    if (value.AddressLatitude == null || value.AddressLongitude == null) return;
                    setMarkerList(prevMarkerList => [
                        ...prevMarkerList,
                        {
                            geocode: [value.AddressLatitude, value.AddressLongitude],
                            popUpData: {
                                "lastName": value.CustomerTitle ? value.CustomerTitle : "",
                                "name": value.Name ? value.Name : "",
                                "address": value.Address ? value.Address : "",
                                "phone": value.phone ? value.phone : "",
                                "mobileNumber": value.mobileNumber ? value.mobileNumber : "",
                                "date": value.LastVisitDate ? (moment(value.LastVisitDate, 'DD-MM-YYYY').format("MM/DD/YYYY")) : ""
                            }
                        }
                    ]);
                });
            }
            setIsMapLoader(false);
        }).catch((error) => {
            console.log(error);
            if(error.response && (error.response.status === 401 || error.response.status === 404)) {
                localStorage.removeItem("token");
                localStorage.removeItem("userInfo");
                window.location.reload();
            }
        });
    };
    const getIpJson = async () => {
        try {
            const response = await axios.get("https://ipapi.co/json/", {
                headers: {
                    "Accept": "application/json",
                    "Content-Type": "application/json",
                },
            });
            setIpData(response.data);
        } catch (error) {
            console.error("Error fetching IP data:", error);
        }
    };
    const getVisitTypeListApi = async(url, token) => {
        const headers = {
            "Accept" : "Application/json",
            "Content-Type" : "Application/json",
            "Authorization" : `Bearer ${token}`
        };

        await axios.get(url+`/api/OfflineSync/GetCustomerVisitType`,{ headers })
        .then((response) => {
            setVisitTypeList(response.data);
        }).catch((error) => {
            console.log(error);
            if(error.response && (error.response.status === 401 || error.response.status === 404)) {
                localStorage.removeItem("token");
                localStorage.removeItem("userInfo");
                window.location.reload();
            }
        });
    };
    const getDeactivateReasonListApi = async(url, token) => {
        const headers = {
            "Accept" : "Application/json",
            "Content-Type" : "Application/json",
            "Authorization" : `Bearer ${token}`
        };

        await axios.get(url+`/api/OfflineSync/GetAllCustomerDeActivateReason`,{ headers })
        .then((response) => {
            setDeactivateReasonList(response.data);
        }).catch((error) => {
            console.log(error);
            if(error.response && (error.response.status === 401 || error.response.status === 404)) {
                localStorage.removeItem("token");
                localStorage.removeItem("userInfo");
                window.location.reload();
            }
        });
    };
    const getTransactionListApi = async(url, token) => {
        const headers = {
            "Accept" : "Application/json",
            "Content-Type" : "Application/json",
            "Authorization" : `Bearer ${token}`
        };

        await axios.get(url+`/api/Customer/GetCustomerTransactionList?customerid=${id}`,{ headers })
        .then((response) => {
            const sortedAsc = [...response.data].sort((a, b) =>
                moment(a.DocumentDate).isBefore(moment(b.DocumentDate)) ? -1 : 1
            );
            setDefaultList(sortedAsc);

            let previousBalance = 0;
            const transactionsWithBalanceAsc = sortedAsc.map(transaction => {
                const balance = previousBalance + (transaction?.Credit ? (transaction.Credit) : (transaction.Debit));
                previousBalance = balance;
                return { ...transaction, Balance: balance };
            });
            setLastCalculatedBalance(previousBalance);

            const transactionsWithBalanceDesc = transactionsWithBalanceAsc.reverse();
            setTransactionList(transactionsWithBalanceDesc);
            setOldTransactionData(transactionsWithBalanceDesc);
        }).catch((error) => {
            console.log(error);
            if(error.response && (error.response.status === 401 || error.response.status === 404)) {
                localStorage.removeItem("token");
                localStorage.removeItem("userInfo");
                window.location.reload();
            }
        });
    };
    const getRecommendedProductListApi = async(url, token) => {
        setIsRecommendedPrdouctLoader(true);
        const headers = {
            "Accept" : "Application/json",
            "Content-Type" : "Application/json",
            "Authorization" : `Bearer ${token}`
        };

        await axios.get(url+`/api/OfflineSync/GetRecommendedProductsList?customerid=${id}`,{ headers })
        .then((response) => {
            setRecommendedProductList(response.data);
            setIsRecommendedPrdouctLoader(false);
        }).catch((error) => {
            console.log(error);
            if(error.response && (error.response.status === 401 || error.response.status === 404)) {
                localStorage.removeItem("token");
                localStorage.removeItem("userInfo");
                window.location.reload();
            }
        });
    };
    const getAttachmentListApi = async(url, token) => {
        setIsAttachmentLoader(true);
        const headers = {
            "Accept" : "Application/json",
            "Content-Type" : "Application/json",
            "Authorization" : `Bearer ${token}`
        };

        await axios.get(url+`/api/OfflineSync/GetCustomerAttachments?customerid=${id}`,{ headers })
        .then((response) => {
            setAttachmentList(response.data);
            setIsAttachmentLoader(false);
        }).catch((error) => {
            console.log(error);
            if(error.response && (error.response.status === 401 || error.response.status === 404)) {
                localStorage.removeItem("token");
                localStorage.removeItem("userInfo");
                window.location.reload();
            }
        });
    };
    const getAgreementListApi = async(url, token) => {
        setIsAgreementLoader(true);
        const headers = {
            "Accept" : "Application/json",
            "Content-Type" : "Application/json",
            "Authorization" : `Bearer ${token}`
        };

        await axios.get(url+`/api/OfflineSync/GetAgreementCustomerWise?customerid=${id}`,{ headers })
        .then((response) => {
            setAgreementList(response.data);
            setIsAgreementLoader(false);
        }).catch((error) => {
            console.log(error);
            if(error.response && (error.response.status === 401 || error.response.status === 404)) {
                localStorage.removeItem("token");
                localStorage.removeItem("userInfo");
                window.location.reload();
            }
        });
    };
    const getAllAssignedAssetListApi = async(url, token) => {
        setIsAssetsLoader(true);
        const headers = {
            "Accept" : "Application/json",
            "Content-Type" : "Application/json",
            "Authorization" : `Bearer ${token}`
        };

        await axios.get(url+`/api/Assets/GetAllAssignedAssets?customer_id=${id}`,{ headers })
        .then((response) => {
            setAllAssignedAssetList(response.data);
            setIsAssetsLoader(false);
        }).catch((error) => {
            console.log(error);
            if(error.response && (error.response.status === 401 || error.response.status === 404)) {
                localStorage.removeItem("token");
                localStorage.removeItem("userInfo");
                window.location.reload();
            }
        });
    };
    const getCustomerCouponListApi = async(url, token) => {
        setIsCouponLoader(false);
        const headers = {
            "Accept" : "Application/json",
            "Content-Type" : "Application/json",
            "Authorization" : `Bearer ${token}`
        };

        await axios.get(url+`/api/OfflineSync/GetCustomerCouponList?customerid=${id}`, { headers })
        .then((response) => {
            setCouponList(response.data);
            setIsCouponLoader(false);
        }).catch((error) => {
            console.log(error);
            if(error.response && (error.response.status === 401 || error.response.status === 404)) {
                localStorage.removeItem("token");
                localStorage.removeItem("userInfo");
                window.location.reload();
            }
        });
    };
    // handle functions
    const handleOpenSchedulePopUp = (data) => {
        if(data === 'start') {
            setShowSchedulePopUp(!showSchedulePopUp)
        } else {
            setVisitTypeData({});
            localStorage.removeItem("visit");
        }
    };
    const handleVisitResponseDataFromChild = (data) => {
        setVisitTypeData(data);
        setShowSchedulePopUp(!showSchedulePopUp);
    };
    const handleWarningPopUpDataFromChild = (data) => {
        setShowWarningPopUp(!showWarningPopUp);
        if(data === 'complete') {
            setVisitTypeData({});
        }
    };
    const handleDeactivePopUpDataFromChild = (data) => {
        if(data === 'updated') {
            getCustomerDataApi(baseUrl, token);
        }
        setShowDeactivePopUp(false);
    };
    const openTab = (e, value) => {
        e.preventDefault();
        setShowTab(value);
        localStorage.setItem('profile_tab', value);
    };
    const handleShowMapDataFromChild = (data) => {
        setShowMap(data);
    };
    const updateAttachmentList = (newAttachments) => {
        setAttachmentList(newAttachments);
    };
    const handleUpdatedRequestFromChild = () => {
        getAllAssignedAssetListApi(baseUrl, token);
    };
    const handleCurrentPage = (data) => {
        setCurrentPage(1);
    };
    const handleDateDataFromChild = (data) => {
        setCurrentPage(1);

        let previousBalance = 0;
        const transactionsWithBalanceAsc = defaultList.map(transaction => {
            const balance = previousBalance;
            previousBalance = balance + (transaction?.Credit ? (transaction.Credit) : (transaction.Debit));
            return { ...transaction, Balance: balance };
        });

        const fromDate = moment(data.fromDate, 'YYYY-MM-DD');
        const toDate = moment(data.toDate, 'YYYY-MM-DD');

        const filteredPrevious = transactionsWithBalanceAsc.filter(txn => {
            const txnDate = moment(txn.DocumentDate, 'YYYY-MM-DD');
            return txnDate.isSameOrAfter(fromDate, 'day') && txnDate.isSameOrBefore(toDate, 'day');
        });
        const filtered = oldTransactionData.filter(txn => {
            const txnDate = moment(txn.DocumentDate, 'YYYY-MM-DD');
            return txnDate.isSameOrAfter(fromDate, 'day') && txnDate.isSameOrBefore(toDate, 'day');
        });

        if(filteredPrevious.length > 0) {
            const addData = {
                "CustomerId": id,
                "Document": "",
                "DocumentDiscription": "previous_balance_check_added",
                "DocumentDate": "",
                "Credit": "",
                "Debit": "",
                "VATValue": "",
                "TotalValue": "",
                "Document_comment": "previous balance",
                "Balance": (filteredPrevious.length > 0) ? filteredPrevious[0].Balance : 0
            };
            const mergedArray = [...filtered, addData];
            setTransactionList(mergedArray);
        } else {
            setTransactionList(filtered);
        }
    };
    const clearFilter = () => {
        setTransactionList(oldTransactionData);
    };

    return (
        <div className="relative">
            <Layout sendTabCheckDataFromChild={handleTabCheckDataFromChild}>
                <div className="relative">
                    <ProfileLayout page={"profile"} customerData={customerData} visitTypeData={visitTypeData} sendOpenSchedulePopUp={handleOpenSchedulePopUp} sendPageChange={handlePageLinkChange} />
                    { isLoader
                        ?
                            <LoaderIcon />
                        :
                        <div className="space-y-2 sm:space-y-4">
                            <div className="p-2 sm:p-4 bg-white border border-white rounded-lg space-y-4 shadow-around">
                                <div className="grid grid-cols-8 gap-4 items-center">
                                    <div className="col-span-6 sm:col-span-4 sm:space-y-2">
                                        <div>
                                            <h1 className="font-semibold lg:text-lg">{customerData.name}</h1>
                                        </div>
                                        <div className="font-normal text-xs lg:text-sm text-gray-dark capitalize">
                                            { t('customer code') }: <span className="text-black">{customerData.code}</span>
                                        </div>
                                        {customerData.address ? 
                                            <div className="hidden sm:flex items-center text-sm">
                                                <div className="flex">
                                                    <img src="../../../assets/icons/location.svg" alt="mail" />
                                                    <div className="text-gray-dark ml-1">{customerData.address}</div>
                                                </div>
                                            </div>
                                            : null
                                        }
                                    </div>
                                    
                                    {(showTab === "profile") &&
                                        <div className="col-span-2 sm:col-span-4 flex justify-end space-x-3">
                                            <div className="hidden sm:flex items-center space-x-3 justify-end">
                                                <div className="flex justify-between items-center space-x-3">
                                                    <div className="bg-gray-dark3 rounded-lg p-1.5 lg:p-3 flex items-center space-x-1 text-xs sm:text-[10px] lg:text-sm">
                                                        <img className="w-4 h-4" src="../../assets/icons/profile/calendar.svg" alt="calendar" />
                                                        <span className="capitalize">{t('last visit date')}: <strong>{customerData.lastVisitedDate ? moment(customerData.lastVisitedDate).format('LL') : '--' }</strong></span>
                                                    </div>
                                                    {(customerData.isActive
                                                        ?
                                                            <div onClick={(e) => setShowDeactivePopUp(!showDeactivePopUp)} className="underline text-yellow-normal font-semibold capitalize cursor-pointer text-xs lg:text-base">
                                                                { t("deactivate")} 
                                                            </div>
                                                        :
                                                            <div>
                                                                <button className="text-red-normal cursor-not-allowed rounded-full font-medium px-4 py-1 capitalize bg-red-light text-xs sm:text-sm">{ t('deactivated')}</button>
                                                            </div>
                                                        )
                                                    }
                                                </div>
                                                <div onClick={(e) => (customerData.isActive ? navigate(`/profiles/edit/${id}`) : null)} >
                                                    <div className={(customerData.isActive ? "cursor-pointer" : "cursor-not-allowed") + " flex items-center space-x-1 text-xs lg:text-base"}>
                                                        <img className="w-4 h-4" src="../../assets/icons/dashboard/edit.svg" alt="edit" />
                                                        <span className="capitalize text-light-400 font-medium">{ t('edit')}</span>
                                                    </div>
                                                </div>
                                            </div>
                                            <div onClick={(e) => (customerData.isActive ? navigate(`/profiles/edit/${id}`) : null)} className="block sm:hidden">
                                                <div className={(customerData.isActive ? "cursor-pointer" : "cursor-not-allowed") + " flex items-center space-x-1 text-xs lg:text-base justify-end"}>
                                                    <img className="w-4 h-4" src="../../assets/icons/dashboard/edit.svg" alt="edit" />
                                                    <span className="capitalize text-light-400 font-medium">{ t('edit')}</span>
                                                </div>
                                            </div>
                                        </div>
                                    }
                                    {(showTab === "transaction") && 
                                        <div className="col-span-2 sm:col-span-4 hidden sm:flex items-center space-x-6 justify-end">
                                            <div className="flex items-center space-x-1">
                                                <h1 className="text-light-400 font-medium capitalize">{t ('balance')}: </h1>
                                                <div className="text-green-normal font-bold">{formatter.format(lastCalculatedBalance)} €</div>
                                            </div>
                                            <div className="flex items-center space-x-2">
                                                {/* <SortFilterDropdown sendSortDataFromChild={handleSortDataFromChild} /> */}
                                                <DateFilterDropdown sendDateDataFromChild={handleDateDataFromChild} sendCloseDataFromChild={clearFilter} />
                                            </div>
                                        </div>
                                    }
                                </div>
                                <div className="flex justify-between md:justify-normal space-x-3 md:space-x-6 overflow-auto no-scrollbar">
                                    <div onClick={(e) => openTab(e, 'profile')} className={(showTab === "profile" ? activeTab : "text-gray-dark") + " cursor-pointer text-center text-xs sm:text-base font-semibold capitalize"}><span className="flex w-max">{ t('profile') }</span></div>
                                    <div onClick={(e) => openTab(e, 'transaction')} className={(showTab === "transaction" ? activeTab : "text-gray-dark") + " cursor-pointer text-center text-xs sm:text-base font-semibold capitalize"}><span className="flex w-max">{ t('transaction') }</span></div>
                                    <div onClick={(e) => openTab(e, 'recommended-products')} className={(showTab === "recommended-products" ? activeTab : "text-gray-dark") + " cursor-pointer text-center text-xs sm:text-base font-semibold capitalize"}><span className="flex w-max">{ t('recommended products') }</span></div>
                                    <div onClick={(e) => openTab(e, 'show-on-map')} className={(showTab === "show-on-map" ? activeTab : "text-gray-dark") + " cursor-pointer text-center text-xs sm:text-base font-semibold capitalize "}><span className="flex w-max">{ t('show on map') }</span></div>
                                    <div onClick={(e) => openTab(e, 'attachments')} className={(showTab === "attachments" ? activeTab : "text-gray-dark") + " cursor-pointer text-center text-xs sm:text-base font-semibold capitalize"}><span className="flex w-max">{ t('attachments') }</span></div>
                                    <div onClick={(e) => openTab(e, 'agreements')} className={(showTab === "agreements" ? activeTab : "text-gray-dark") + " cursor-pointer text-center text-xs sm:text-base font-semibold capitalize"}><span className="flex w-max">{ t('agreements') }</span></div>
                                    <div onClick={(e) => openTab(e, 'assets')} className={(showTab === "assets" ? activeTab : "text-gray-dark") + " cursor-pointer text-center text-xs sm:text-base font-semibold capitalize"}><span className="flex w-max">{ t('assets') }</span></div>
                                    <div onClick={(e) => openTab(e, 'coupons')} className={(showTab === "coupons" ? activeTab : "text-gray-dark") + " cursor-pointer text-center text-xs sm:text-base font-semibold capitalize"}><span className="flex w-max">{ t('coupons') }</span></div>
                                </div>
                            </div>
                            {(showTab === "profile") && <ProfileComponent customerData={customerData} markers={markers} sendShowMapDataFromChild={handleShowMapDataFromChild} />}
                            {(showTab === "transaction") && <TransactionComponent transactionsList={transactionList} lastCalculatedBalance={lastCalculatedBalance} defaultData={defaultList} rowCurrentPageData={rowCurrentPage} sendCurrentPage={handleCurrentPage} oldTransactionData={oldTransactionData} />}
                            {(showTab === "recommended-products") && (isRecommendedPrdouctLoader ? <TableLoaderIcon /> : <RecommendedProductsComponent defaultProductImage={defaultProductImage} recommendedProductList={recommendedProductList} />)}
                            {(showTab === "show-on-map") && (isMapLader ? <TableLoaderIcon /> :
                                <div className="relative p-1 sm:p-4 w-full max-h-full bg-white rounded-lg shadow-around">
                                    <MapComponent markers={markerList} draggable={false} radius={userData.radius ?? 0}/>
                                    <div className="absolute top-6 z-20 flex flex-col items-center justify-between w-full">
                                        <div className="bg-white py-2 px-4 flex justify-between text-light-700 rounded-xl space-x-2">
                                            <div className="capitalize text-sm">{ t('total customers')}<span className="font-medium">: {totalCustomer}</span></div>
                                            <div className="border h-4 border-black-100"></div>
                                            <div className="capitalize text-light-700 text-sm">{ t('radius')}<span className="font-medium">: {userData.radius ? userData.radius : 0}m</span></div>
                                        </div>
                                    </div>
                                </div>
                            )}
                            {(showTab === "attachments") && (isAttachmentLoader ? <TableLoaderIcon /> : <AttachmentComponent isActive={customerData.isActive} customerId={id} attachmentList={attachmentList} updateAttachmentList={updateAttachmentList} />)}
                            {(showTab === "agreements") && (isAgreementLoader ? <TableLoaderIcon /> : <AgreementComponent isActive={customerData.isActive} customerId={id} agreementList={agreementList}/>)}
                            {(showTab === "assets") && (isAssetsLoader ? <TableLoaderIcon /> : <AssetsComponent isActive={customerData.isActive} customerId={id} defaultProductImage={defaultProductImage} allAssignedAssetList={allAssignedAssetList} sendUpdatedRequestFromChild={handleUpdatedRequestFromChild} />)}
                            {(showTab === "coupons") && (isCouponLoader ? <TableLoaderIcon /> : <CouponComponent isActive={customerData.isActive} customerId={id} defaultProductImage={defaultProductImage} couponList={couponList}/>)}
                        </div>
                    }
                </div>
            </Layout>
            {/* page */}
            {(showTab === "profile") &&
                <div className="fixed bottom-0 sm:hidden flex items-center bg-white rounded-t-xl border border-background-normal justify-between p-3 shadow-md w-screen">
                    <div>
                        <div className="capitalize text-[11px]">{t('last visited on')}:</div>
                        <div className="font-semibold text-[11px]">{customerData.lastVisitedDate ? moment(customerData.lastVisitedDate).format('LL') : '--'}</div>
                    </div>
                    {customerData.isActive 
                        ?
                            <div onClick={(e) => setShowDeactivePopUp(!showDeactivePopUp)} className="underline text-yellow-normal font-semibold capitalize cursor-pointer text-xs">
                                { t("deactivate")} 
                            </div>
                        :
                            <div>
                                <button className="text-red-normal cursor-not-allowed rounded-full font-medium px-3 py-1 capitalize bg-red-light text-xs sm:text-sm">deactivated</button>
                            </div>
                    }
                    {/* <div className={((visitTypeData && visitTypeData.visitId) ? "cursor-pointer text-white bg-blue-normal border border-blue-normal" : "text-white border-blue-disable cursor-not-allowed bg-blue-disable") + " text-center text-xs px-2 sm:px-4 font-medium py-2 rounded-lg tracking-wide first-letter:capitalize"}>{ t('schedule a visit') }</div> */}
                </div>
            }
            {showMap &&
                <div className="overflow-y-auto overflow-x-hidden bg-blue-300 bg-opacity-50 fixed flex flex-col top-0 right-0 left-0 z-50 justify-center items-center w-full inset-0 max-h-full">
                    <div className="relative p-1 sm:p-4 w-full max-w-5xl max-h-full bg-white rounded-lg">
                        <button onClick={(e) => setShowMap(!showMap)} className="absolute right-1 top-1 border border-gray-400 shadow p-3 bg-white z-20 text-gray-400 rounded-full" >
                            <svg className="w-3 h-3" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 14 14">
                                <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="m1 1 6 6m0 0 6 6M7 7l6-6M7 7l-6 6"/>
                            </svg>    
                        </button>
                        <MapComponent markers={markers} draggable={false} radius={0}/>
                    </div>
                </div>
            }
            {showDeactivePopUp && <DeactivatePopUp customerId={id} deactivateReasonList={deactivateReasonList} sendDeactivePopUpDataFromChild={handleDeactivePopUpDataFromChild} />}
            {/* common */}
            {showSchedulePopUp && <SchedulePopUp customerId={id} ipData={ipData} visitTypeList={visitTypeList} sendSchedulePopUpDataFromChild={handleOpenSchedulePopUp} sendVisitResponseDataFromChild={handleVisitResponseDataFromChild} />}
            {showWarningPopUp && <WarningPopUp customerId={id} visitTypeData={visitTypeData} sendWarningPopUpDataFromChild={handleWarningPopUpDataFromChild} /> }
        </div>
    )
}

export default ProfileView;